import React, { useEffect, useRef, useState, useContext } from 'react';
import { GoogleMapsContext } from '../App'; 
import { City } from '../types/trip';
import { generateDistinctPastelColor } from '../utils/colorUtils';
import { TripEventBus, TRIP_EVENTS } from '../contexts/TripContext';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 54.5,
  lng: -4.0
};

interface MarkerPosition {
  lat: number;
  lng: number;
}

interface CityColor {
  city: string;
  color: string;
}

interface MapComponentProps {
  transitRoutes?: Record<string, any>;
  getTransitRoute?: (fromCity: string, toCity: string) => any;
  updateTransitRoute?: (fromCity: string, toCity: string, routeData: any) => void;
  locations: string[];
  cityColors: CityColor[];
  cities?: City[]; 
  selectedCountry?: string; 
  mapId?: string;
  mapOptions?: {
    zoomControl?: boolean;
    mapTypeControl?: boolean;
    fullscreenControl?: boolean;
    disableAllControls?: boolean;
    initialZoom?: number;
  };
  previewLocation?: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    temporary: boolean;
  } | null;
}

// Default route colors
const DEFAULT_ROUTE_COLOR = '#888888'; // Grey
const HIGHLIGHTED_ROUTE_COLOR = '#4285F4'; // Blue

const MapComponent: React.FC<MapComponentProps> = ({ 
  locations, 
  cityColors, 
  cities, 
  selectedCountry, 
  mapId = "map", 
  mapOptions = {
    zoomControl: true,
    mapTypeControl: true,
    fullscreenControl: true,
    disableAllControls: false
  },
  previewLocation,
  getTransitRoute,
  updateTransitRoute,
  transitRoutes
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const directionsRenderersRef = useRef<google.maps.DirectionsRenderer[]>([]);
  const activeInfoWindow = useRef<google.maps.InfoWindow | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const geocodedLocationsRef = useRef<Map<string, MarkerPosition>>(new Map());
  const previousLocationsRef = useRef<string[]>([]);
  const isGoogleMapsLoaded = useContext(GoogleMapsContext);
  const previewMarkerRef = useRef<google.maps.Marker | null>(null);
  const previewInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const mapInitializedRef = useRef<boolean>(false);
  
  // Store route connections between cities for hover effects
  const routeConnectionsRef = useRef<
    Map<string, { incomingRoute?: google.maps.DirectionsRenderer; outgoingRoute?: google.maps.DirectionsRenderer }>
  >(new Map());

  // Initialize map when Google Maps script is loaded
  useEffect(() => {
    let isMounted = true;
    let initTimeout: NodeJS.Timeout;

    const initializeMap = () => {
      // Prevent re-initialization if map is already created
      if (mapRef.current || mapInitializedRef.current || !isMounted) {
        return;
      }

      // Ensure Google Maps API is fully loaded with proper safety checks
      if (!window.google || !window.google.maps || !(window.google.maps as any).Map) {
        console.warn('Google Maps API not fully loaded yet, retrying...');
        return false;
      }

      try {
        const mapElement = document.getElementById(mapId);
        if (!mapElement) {
          console.error(`Map element with id "${mapId}" not found`);
          return false;
        }

        console.log('Initializing Google Maps...');
        const map = new (google.maps as any).Map(mapElement, {
          center: defaultCenter,
          zoom: mapOptions.initialZoom !== undefined ? mapOptions.initialZoom : 3,
          disableDefaultUI: mapOptions.disableAllControls || (!mapOptions.mapTypeControl && !mapOptions.zoomControl && !mapOptions.fullscreenControl),
          mapTypeControl: mapOptions.disableAllControls ? false : mapOptions.mapTypeControl,
          zoomControl: mapOptions.disableAllControls ? false : mapOptions.zoomControl,
          streetViewControl: false,
          fullscreenControl: mapOptions.disableAllControls ? false : mapOptions.fullscreenControl,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });
      
        mapRef.current = map;
        mapInitializedRef.current = true;
        setIsMapReady(true);
        console.log('Google Maps initialized successfully');
        return true;
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        return false;
      }
    };

    const attemptInitialization = () => {
      const hasGoogleMaps = !!(window.google && window.google.maps && (window.google.maps as any).Map);
      
      console.log('Attempting map initialization...', {
        isGoogleMapsLoaded,
        hasWindow: !!window.google,
        hasMaps: !!window.google?.maps,
        hasMapConstructor: hasGoogleMaps
      });

      // Try to initialize immediately if everything is ready
      if (isGoogleMapsLoaded && hasGoogleMaps) {
        if (initializeMap()) return;
      }

      // Fallback: Check periodically for Google Maps availability
      let attempts = 0;
      const maxAttempts = 30; // 3 seconds with 100ms intervals
      
      const checkInterval = setInterval(() => {
        attempts++;
        console.log(`Checking for Google Maps API, attempt ${attempts}/${maxAttempts}`);
        
        const isReady = !!(window.google && window.google.maps && (window.google.maps as any).Map);
        if (isReady) {
          console.log('Google Maps API found, initializing...');
          clearInterval(checkInterval);
          initializeMap();
        } else if (attempts >= maxAttempts) {
          console.error('Failed to load Google Maps API after maximum attempts');
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup interval on unmount
      return () => clearInterval(checkInterval);
    };

    // Small delay to ensure DOM is ready
    initTimeout = setTimeout(attemptInitialization, 100);
    
    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      
      // Only clean up markers and routes, don't destroy the map
      markersRef.current.forEach(marker => {
        marker.setMap(null);
      });
      markersRef.current.clear();
      
      polylinesRef.current.forEach(polyline => {
        polyline.setMap(null);
      });
      polylinesRef.current = [];
      
      directionsRenderersRef.current.forEach(renderer => {
        renderer.setMap(null);
      });
      directionsRenderersRef.current = [];
      
      if (previewMarkerRef.current) {
        previewMarkerRef.current.setMap(null);
        previewMarkerRef.current = null;
      }
      
      if (previewInfoWindowRef.current) {
        previewInfoWindowRef.current.close();
        previewInfoWindowRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded, mapId, mapOptions]); // Depend on isGoogleMapsLoaded

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !selectedCountry) return;
  
    if (!cities || cities.length === 0) {
      const geocoder = new google.maps.Geocoder();
      
      geocoder.geocode({ address: selectedCountry }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const bounds = results[0].geometry.viewport;
          if (bounds && mapRef.current) {
            // Calculate the dimensions of the country
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            
            // Calculate width and height in degrees
            const latSpan = Math.abs(ne.lat() - sw.lat());
            const lngSpan = Math.abs(ne.lng() - sw.lng());
            
            // Calculate appropriate zoom level
            // These thresholds can be adjusted based on your needs
            let zoom: number;
            if (latSpan > 50 || lngSpan > 50) {
              zoom = 3;  // Very large countries (Russia, USA, China)
            } else if (latSpan > 25 || lngSpan > 25) {
              zoom = 4;  // Large countries (France, Germany)
            } else if (latSpan > 10 || lngSpan > 10) {
              zoom = 5;  // Medium countries (UK, Italy)
            } else if (latSpan > 5 || lngSpan > 5) {
              zoom = 6;  // Small countries (Netherlands, Switzerland)
            } else {
              zoom = 7;  // Very small countries (Singapore, Luxembourg)
            }
  
            // Fit to bounds first
            mapRef.current.fitBounds(bounds);
            
            const padding = {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50
            };
  
            setTimeout(() => {
              // Apply calculated zoom and center
              mapRef.current?.fitBounds(bounds, padding);
              mapRef.current?.setZoom(zoom);
              
              // Center on the middle of the bounds
              mapRef.current?.setCenter({
                lat: (ne.lat() + sw.lat()) / 2,
                lng: (ne.lng() + sw.lng()) / 2
              });
            }, 100);
          }
        } else {
          console.warn('Failed to geocode country:', status);
          mapRef.current?.setCenter(defaultCenter);
          mapRef.current?.setZoom(3);
        }
      });
    }
  }, [isMapReady, selectedCountry, cities]);

  // Function to highlight routes connected to a city
  const highlightConnectedRoutes = (cityId: string) => {
    // First check stored route connections
    const routeConnection = routeConnectionsRef.current.get(cityId);
    let highlighted = false;
    
    if (routeConnection) {
      // Highlight incoming route if exists
      if (routeConnection.incomingRoute) {
        routeConnection.incomingRoute.setOptions({
          polylineOptions: {
            strokeColor: HIGHLIGHTED_ROUTE_COLOR,
            strokeOpacity: 1.0,
            strokeWeight: 4
          }
        });
        routeConnection.incomingRoute.setMap(mapRef.current);
        highlighted = true;
      }
      
      // Highlight outgoing route if exists
      if (routeConnection.outgoingRoute) {
        routeConnection.outgoingRoute.setOptions({
          polylineOptions: {
            strokeColor: HIGHLIGHTED_ROUTE_COLOR,
            strokeOpacity: 1.0,
            strokeWeight: 4
          }
        });
        routeConnection.outgoingRoute.setMap(mapRef.current);
        highlighted = true;
      }
    }
    
    // If we didn't find routes in the connection map, 
    // we may need to check polylines directly
    if (!highlighted && cities) {
      // Find the city in our cities array
      const cityIndex = cities.findIndex(city => city.id === cityId);
      if (cityIndex === -1) return;
      
      // Check both incoming and outgoing polylines 
      // (from previous city and to next city)
      if (cityIndex > 0 && polylinesRef.current[cityIndex - 1]) {
        polylinesRef.current[cityIndex - 1].setOptions({
          strokeColor: HIGHLIGHTED_ROUTE_COLOR,
          strokeOpacity: 1.0,
          strokeWeight: 4
        });
      }
      
      if (cityIndex < cities.length - 1 && polylinesRef.current[cityIndex]) {
        polylinesRef.current[cityIndex].setOptions({
          strokeColor: HIGHLIGHTED_ROUTE_COLOR,
          strokeOpacity: 1.0,
          strokeWeight: 4
        });
      }
    }
  };

  // Function to reset routes to default style
  const resetRouteHighlighting = (cityId: string) => {
    // First check stored route connections
    const routeConnection = routeConnectionsRef.current.get(cityId);
    let reset = false;
    
    if (routeConnection) {
      // Reset incoming route if exists
      if (routeConnection.incomingRoute) {
        routeConnection.incomingRoute.setOptions({
          polylineOptions: {
            strokeColor: DEFAULT_ROUTE_COLOR,
            strokeOpacity: 0.8,
            strokeWeight: 3
          }
        });
        routeConnection.incomingRoute.setMap(mapRef.current);
        reset = true;
      }
      
      // Reset outgoing route if exists
      if (routeConnection.outgoingRoute) {
        routeConnection.outgoingRoute.setOptions({
          polylineOptions: {
            strokeColor: DEFAULT_ROUTE_COLOR,
            strokeOpacity: 0.8,
            strokeWeight: 3
          }
        });
        routeConnection.outgoingRoute.setMap(mapRef.current);
        reset = true;
      }
    }
    
    // If we didn't find routes in the connection map, 
    // we may need to check polylines directly
    if (!reset && cities) {
      // Find the city in our cities array
      const cityIndex = cities.findIndex(city => city.id === cityId);
      if (cityIndex === -1) return;
      
      // Reset both incoming and outgoing polylines
      if (cityIndex > 0 && polylinesRef.current[cityIndex - 1]) {
        polylinesRef.current[cityIndex - 1].setOptions({
          strokeColor: DEFAULT_ROUTE_COLOR, 
          strokeOpacity: 0.8,
          strokeWeight: 3
        });
      }
      
      if (cityIndex < cities.length - 1 && polylinesRef.current[cityIndex]) {
        polylinesRef.current[cityIndex].setOptions({
          strokeColor: DEFAULT_ROUTE_COLOR,
          strokeOpacity: 0.8,
          strokeWeight: 3
        });
      }
    }
  };

  // Function to show info window for a city
  const showCityInfoWindow = (cityId: string) => {
    const marker = markersRef.current.get(cityId);
    if (!marker || !mapRef.current) return;
    
    // Close any existing info window
    if (activeInfoWindow.current) {
      activeInfoWindow.current.close();
    }
    
    // Find the city data
    const city = cities?.find(c => c.id === cityId);
    if (!city) return;
    
    // Create info window content
    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="map-info-window">
          <h3>${city.name}</h3>
          <p>${city.country}</p>
        </div>
      `
    });
    
    // Open the info window
    infoWindow.open(mapRef.current, marker);
    activeInfoWindow.current = infoWindow;
  };

  // Function to hide info window
  const hideCityInfoWindow = () => {
    if (activeInfoWindow.current) {
      activeInfoWindow.current.close();
      activeInfoWindow.current = null;
    }
  };

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;
    
    // Clear any existing preview marker
    if (previewMarkerRef.current) {
      previewMarkerRef.current.setMap(null);
      previewMarkerRef.current = null;
    }
    
    // Close any existing preview info window
    if (previewInfoWindowRef.current) {
      previewInfoWindowRef.current.close();
      previewInfoWindowRef.current = null;
    }
    
    // If there's a preview location, create a marker for it
    if (previewLocation && previewLocation.coordinates) {
      // Create a marker with animation
      const marker = new google.maps.Marker({
        position: previewLocation.coordinates,
        map: mapRef.current,
        title: previewLocation.name,
        animation: google.maps.Animation.DROP,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#e74c3c', // Red color for preview
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
          scale: 15,
        },
        zIndex: 1000 // Ensure it appears above other markers
      });
      
      // Store the marker reference
      previewMarkerRef.current = marker;
      
      // Add a bounce animation
      setTimeout(() => {
        if (marker && previewLocation.temporary) {
          marker.setAnimation(google.maps.Animation.BOUNCE);
        }
      }, 500);
      
      // Pan map to show the preview location
      mapRef.current.panTo(previewLocation.coordinates);
      
      // Set appropriate zoom level
      mapRef.current.setZoom(6);
    }
  }, [isMapReady, previewLocation]);

  // Setup event listeners for city card hover events
  useEffect(() => {
    if (!isMapReady || !cities) return;
    
    // Handle start of hover
    const handleCityHoverStart = (cityId: string) => {
      showCityInfoWindow(cityId);
      // Scale up the marker slightly
      const marker = markersRef.current.get(cityId);
      if (marker) {
        const currentIcon = marker.getIcon() as google.maps.Symbol;
        if (currentIcon && typeof currentIcon === 'object') {
          // Scale up the marker slightly
          marker.setIcon({
            ...currentIcon,
            scale: 18, // Increase size from 15 to 18 for subtle effect
            strokeWeight: 3, // Slightly thicker stroke
            strokeColor: '#FFFFFF',
          });
        }
      }
    };
    
    // Handle end of hover
    const handleCityHoverEnd = (cityId: string) => {
      hideCityInfoWindow();
      // Scale down the marker back to normal
      const marker = markersRef.current.get(cityId);
      if (marker) {
        const currentIcon = marker.getIcon() as google.maps.Symbol;
        if (currentIcon && typeof currentIcon === 'object') {
          // Scale back to normal size
          marker.setIcon({
            ...currentIcon,
            scale: 15, // Back to normal size
            strokeWeight: 2, // Back to normal stroke
            strokeColor: '#FFFFFF',
          });
        }
      }
    };
    
    // Subscribe to events
    TripEventBus.on(TRIP_EVENTS.CITY_HOVER_START, handleCityHoverStart);
    TripEventBus.on(TRIP_EVENTS.CITY_HOVER_END, handleCityHoverEnd);
    
    // Clean up event subscriptions when component unmounts
    return () => {
      TripEventBus.off(TRIP_EVENTS.CITY_HOVER_START, handleCityHoverStart);
      TripEventBus.off(TRIP_EVENTS.CITY_HOVER_END, handleCityHoverEnd);
    };
  }, [isMapReady, cities]);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !cities) return;

    // Performance optimization: Only clear and recreate if cities actually changed
    const currentCityIds = Array.from(markersRef.current.keys());
    const newCityIds = cities.map(city => city.id);
    const citiesChanged = currentCityIds.length !== newCityIds.length ||
                         !currentCityIds.every((id, index) => id === newCityIds[index]);

    if (!citiesChanged && cities.length > 0) {
      // Cities haven't changed, just update positions if needed
      cities.forEach(city => {
        const marker = markersRef.current.get(city.id);
        if (marker && city.coordinates && city.coordinates.lat !== 0 && city.coordinates.lng !== 0) {
          const newPosition = new google.maps.LatLng(city.coordinates.lat, city.coordinates.lng);
          marker.setPosition(newPosition);
        }
      });
      return;
    }

    // Fast path: a single city appended to the end -> incrementally add
    const appendedOne = currentCityIds.length > 0 && newCityIds.length === currentCityIds.length + 1 && currentCityIds.every((id, i) => id === newCityIds[i]);
    if (appendedOne) {
      const newCity = cities[cities.length - 1];
      if (newCity.coordinates && newCity.coordinates.lat !== 0 && newCity.coordinates.lng !== 0) {
        const cleanCityName = newCity.name.split(',')[0].trim();
        const markerColor = cityColors.find(cc => cc.city === cleanCityName)?.color || 
                           generateDistinctPastelColor(cleanCityName, cities.map(c => c.name.split(',')[0].trim()));
        const marker = new google.maps.Marker({
          position: newCity.coordinates,
          map: mapRef.current!,
          title: newCity.name,
          label: {
            text: cities.length.toString(),
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#FFFFFF',
            scale: 15,
          },
          animation: google.maps.Animation.DROP,
        });

        const infoWindow = new google.maps.InfoWindow({
          content: `
          <div class="map-info-window">
            <h3>${newCity.name}</h3>
            <p>${newCity.country}</p>
          </div>
        `
        });
        marker.addListener('click', () => {
          if (activeInfoWindow.current) activeInfoWindow.current.close();
          infoWindow.open(mapRef.current!, marker);
          activeInfoWindow.current = infoWindow;
        });

        markersRef.current.set(newCity.id, marker);
        // Initialize route connection entry for the new city
        routeConnectionsRef.current.set(newCity.id, {});

        // Add simple polyline from previous valid city
        if (cities.length > 1) {
          const prevCity = cities[cities.length - 2];
          if (prevCity.coordinates && prevCity.coordinates.lat !== 0 && prevCity.coordinates.lng !== 0) {
            const polyline = new google.maps.Polyline({
              path: [prevCity.coordinates, newCity.coordinates],
              geodesic: true,
              strokeColor: DEFAULT_ROUTE_COLOR,
              strokeOpacity: 0.8,
              strokeWeight: 3,
              map: mapRef.current!
            });
            polylinesRef.current.push(polyline);
          }
        }

        // Fit bounds to include all markers
        const bounds = new google.maps.LatLngBounds();
        markersRef.current.forEach(m => bounds.extend(m.getPosition() as google.maps.LatLng));
        if (!bounds.isEmpty()) {
          requestAnimationFrame(() => {
            mapRef.current?.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
            if (cities.length === 1) {
              setTimeout(() => mapRef.current?.setZoom(6), 100);
            }
          });
        }

        return; // Skip full rebuild
      }
    }

    // General path: clear and rebuild
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current.clear();

    // Clear existing polylines
    polylinesRef.current.forEach(line => line.setMap(null));
    polylinesRef.current = [];
    
    // Clear existing directions renderers
    directionsRenderersRef.current.forEach(renderer => renderer.setMap(null));
    directionsRenderersRef.current = [];
    
    // Clear route connections
    routeConnectionsRef.current.clear();

    if (cities.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    
    // Function to check if two markers are too close
    const areMarkersOverlapping = (pos1: google.maps.LatLng | google.maps.LatLngLiteral, pos2: google.maps.LatLng | google.maps.LatLngLiteral, threshold = 0.01) => {
      const lat1 = typeof pos1.lat === 'function' ? pos1.lat() : pos1.lat;
      const lng1 = typeof pos1.lng === 'function' ? pos1.lng() : pos1.lng;
      const lat2 = typeof pos2.lat === 'function' ? pos2.lat() : pos2.lat;
      const lng2 = typeof pos2.lng === 'function' ? pos2.lng() : pos2.lng;
      
      const distance = Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lng1 - lng2, 2));
      return distance < threshold;
    };

    // Adjust marker positions to prevent overlap
    const adjustedPositions = cities.map((city, index) => {
      if (!city.coordinates || (city.coordinates.lat === 0 && city.coordinates.lng === 0)) {
        return city.coordinates;
      }

      let adjustedPos = { ...city.coordinates };
      
      // Check against all previous cities
      for (let i = 0; i < index; i++) {
        const prevCity = cities[i];
        if (!prevCity.coordinates) continue;
        
        if (areMarkersOverlapping(adjustedPos, prevCity.coordinates)) {
          // Offset the marker slightly
          const offsetAmount = 0.008; // Small offset
          const angle = (index * 60) % 360; // Different angle for each overlapping marker
          const offsetLat = offsetAmount * Math.cos(angle * Math.PI / 180);
          const offsetLng = offsetAmount * Math.sin(angle * Math.PI / 180);
          
          adjustedPos = {
            lat: adjustedPos.lat + offsetLat,
            lng: adjustedPos.lng + offsetLng
          };
        }
      }
      
      return adjustedPos;
    });

    // Create markers for each city
    cities.forEach((city, index) => {
      if (!city.coordinates || (city.coordinates.lat === 0 && city.coordinates.lng === 0)) return;

      // Find the color from cityColors prop instead of generating a new one
      const cleanCityName = city.name.split(',')[0].trim();
      const markerColor = cityColors.find(cc => cc.city === cleanCityName)?.color || 
                         generateDistinctPastelColor(cleanCityName, cities.map(c => c.name.split(',')[0].trim()));
      const adjustedPosition = adjustedPositions[index];
      
      const marker = new google.maps.Marker({
        position: adjustedPosition,
        map: mapRef.current,
        title: city.name,
        label: {
          text: (index + 1).toString(),
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold'
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
          scale: 15,
        },
        animation: google.maps.Animation.DROP, // Add a subtle drop animation when markers are created
      });

      // Add info window with inline styles to override Google Maps CSS
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="map-info-window" style="font-family: 'Buenos Aires', 'Montserrat', sans-serif !important; padding: 8px !important; min-width: 120px !important;">
            <h3 style="margin: 0 0 4px 0 !important; font-size: 16px !important; font-weight: 600 !important; color: #333 !important; line-height: 1.2 !important; font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;">${city.name}</h3>
            <p style="margin: 0 !important; font-size: 13px !important; color: #333 !important; line-height: 1.3 !important; font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;">${city.country}</p>
          </div>
        `
      });

      // Add click event listener
      marker.addListener('click', () => {
        // Close previously opened info window
        if (activeInfoWindow.current) {
          activeInfoWindow.current.close();
        }
        infoWindow.open(mapRef.current, marker);
        activeInfoWindow.current = infoWindow;
      });

      markersRef.current.set(city.id, marker);
      bounds.extend(city.coordinates);
      
      // Initialize the route connections entry for this city
      routeConnectionsRef.current.set(city.id, {});
    });

    // Draw path between cities if there are multiple cities
    if (cities.length > 1) {
      // Filter out cities without valid coordinates
      const validCities = cities.filter(city => 
        city.coordinates && 
        city.coordinates.lat !== 0 && 
        city.coordinates.lng !== 0
      );

      if (validCities.length > 1) {
        // Loop through adjacent city pairs
        for (let i = 0; i < validCities.length - 1; i++) {
          const fromCity = validCities[i];
          const toCity = validCities[i + 1];
          
          // Check if there's a saved route between these cities
          const routeData = getTransitRoute?.(fromCity.name, toCity.name);
          
          if (routeData && routeData.polyline && google.maps.geometry && google.maps.geometry.encoding) {
            // Use the saved polyline data
            try {
              let decodedPath: google.maps.LatLng[] = [];
              
              // Handle both string and array polylines
              if (Array.isArray(routeData.polyline)) {
                // If it's an array, decode each segment and combine
                routeData.polyline.forEach((polylineSegment: string | undefined) => {
                  if (typeof polylineSegment === 'string') {
                    const segmentPath = google.maps.geometry.encoding.decodePath(polylineSegment);
                    decodedPath = decodedPath.concat(segmentPath);
                  }
                });
              } else if (typeof routeData.polyline === 'string') {
                // If it's a single string, decode it directly
                decodedPath = google.maps.geometry.encoding.decodePath(routeData.polyline);
              }
              
              if (decodedPath.length > 0) {
                const polyline = new google.maps.Polyline({
                  path: decodedPath,
                  geodesic: true,
                  strokeColor: DEFAULT_ROUTE_COLOR,
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                  map: mapRef.current
                });
                
                polylinesRef.current.push(polyline);
              } else {
                // If no valid path was decoded, fall back to requesting directions
                requestDirections(fromCity, toCity, i);
              }
            } catch (error) {
              console.error('Error decoding polyline:', error);
              // Fall back to requesting directions
              requestDirections(fromCity, toCity, i);
            }
          } else {
            // Request directions using Directions API
            requestDirections(fromCity, toCity, i);
          }
        }
      }
    }

    // Optimized map bounds calculation - only if bounds are valid
    if (cities.length > 0 && !bounds.isEmpty()) {
      const padding = {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      };
      
      // Use requestAnimationFrame for smoother map updates
      requestAnimationFrame(() => {
        if (mapRef.current) {
          mapRef.current.fitBounds(bounds, padding);

          // If there's only one city, set a closer zoom
          if (cities.length === 1) {
            setTimeout(() => {
              mapRef.current?.setZoom(6);
            }, 100);
          }
        }
      });
    }
  }, [isMapReady, cities, cityColors]);

  // Remove unwanted X buttons from waypoint markers
  useEffect(() => {
    if (!isMapReady) return;

    const removeUndoButtons = () => {
      // Remove buttons with specific titles/aria-labels
      const undoButtons = document.querySelectorAll(
        'button[title="Undo last edit"], button[aria-label="Undo last edit"], div[title="Undo last edit"], div[aria-label="Undo last edit"]'
      );
      undoButtons.forEach(button => {
        button.remove();
      });

      // Remove InfoWindow close buttons
      const closeButtons = document.querySelectorAll(
        'button[title="Close"], button[aria-label="Close"], .gm-ui-hover-effect[title="Close"], .gm-ui-hover-effect[aria-label="Close"]'
      );
      closeButtons.forEach(button => {
        button.remove();
      });

      // Remove images with undo_poly.png source and their parent containers
      const undoImages = document.querySelectorAll('img[src*="undo_poly.png"], img[src*="undo_poly"]');
      undoImages.forEach(img => {
        const parent = img.closest('div');
        if (parent) {
          parent.remove();
        }
      });
    };

    // Initial removal
    removeUndoButtons();

    // Set up observer to remove buttons when they're dynamically added
    const observer = new MutationObserver(() => {
      removeUndoButtons();
    });

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also set up a periodic check every 500ms to catch any missed buttons
    const interval = setInterval(removeUndoButtons, 500);

    // Cleanup function
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [isMapReady]);

  // Function to request directions between two cities
  const requestDirections = (fromCity: City, toCity: City, index: number) => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    
    // Create a straight line between the two cities
    const path = [fromCity.coordinates, toCity.coordinates];
    const polyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: DEFAULT_ROUTE_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: mapRef.current
    });
    
    polylinesRef.current.push(polyline);
  };

  // Render the component
  return (
    <div 
      id={mapId} 
      style={{ width: '100%', height: '100%' }}
      className="relative"
    >
      {/* Info Window Content Styles - These will be applied by Google Maps */}
      <style jsx global>{`
        .gm-style .gm-style-iw-c {
          padding: 12px !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 7px 1px rgba(0, 0, 0, 0.1) !important;
        }
        .gm-style .gm-style-iw-d {
          overflow: hidden !important;
          padding: 0 !important;
        }
        .gm-style .gm-style-iw-t button {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Hide the waypoint "undo last edit" X button - Multiple selectors to catch all cases */
        button[title="Undo last edit"],
        button[aria-label="Undo last edit"],
        div[title="Undo last edit"],
        div[aria-label="Undo last edit"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Hide InfoWindow close button */
        button[title="Close"],
        button[aria-label="Close"],
        .gm-ui-hover-effect[title="Close"],
        .gm-ui-hover-effect[aria-label="Close"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Hide any button inside Google Maps with these specific attributes */
        .gm-style button[title="Undo last edit"],
        .gm-style button[aria-label="Undo last edit"],
        .gm-style div[title="Undo last edit"],
        .gm-style div[aria-label="Undo last edit"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Hide any image with undo_poly.png source */
        img[src*="undo_poly.png"],
        img[src*="undo_poly"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        /* Hide parent containers of undo buttons */
        div:has(img[src*="undo_poly.png"]) {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        /* More specific selectors to override Google Maps styling */
        .gm-style-iw-d .map-info-window {
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
          padding: 8px !important;
          min-width: 120px !important;
        }
        .gm-style-iw-d .map-info-window h3 {
          margin: 0 0 4px 0 !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          color: #333 !important;
          line-height: 1.2 !important;
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
        }
        .gm-style-iw-d .map-info-window p {
          margin: 0 !important;
          font-size: 13px !important;
          color: #333 !important;
          line-height: 1.3 !important;
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
        }
        /* Fallback selectors */
        .map-info-window {
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
          padding: 8px !important;
          min-width: 120px !important;
        }
        .map-info-window h3 {
          margin: 0 0 4px 0 !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          color: #333 !important;
          line-height: 1.2 !important;
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
        }
        .map-info-window p {
          margin: 0 !important;
          font-size: 13px !important;
          color: #333 !important;
          line-height: 1.3 !important;
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
        }
        /* Additional broad selectors to ensure font applies */
        .gm-style-iw-d div, .gm-style-iw-d h1, .gm-style-iw-d h2, .gm-style-iw-d h3, .gm-style-iw-d h4, .gm-style-iw-d h5, .gm-style-iw-d h6, .gm-style-iw-d p, .gm-style-iw-d span {
          font-family: 'Buenos Aires', 'Montserrat', sans-serif !important;
        }
      `}</style>
    </div>
  );
};

// Optimized comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: MapComponentProps, nextProps: MapComponentProps): boolean => {
  // Quick reference equality checks first
  if (prevProps.mapId !== nextProps.mapId || 
      prevProps.selectedCountry !== nextProps.selectedCountry) {
    return false;
  }

  // Optimized cities comparison - only check length and IDs first
  if (!prevProps.cities && !nextProps.cities) return true;
  if (!prevProps.cities || !nextProps.cities) return false;
  if (prevProps.cities.length !== nextProps.cities.length) return false;
  
  // Check if cities have changed by comparing IDs (much faster than JSON.stringify)
  for (let i = 0; i < prevProps.cities.length; i++) {
    if (prevProps.cities[i].id !== nextProps.cities[i].id ||
        prevProps.cities[i].name !== nextProps.cities[i].name ||
        prevProps.cities[i].coordinates.lat !== nextProps.cities[i].coordinates.lat ||
        prevProps.cities[i].coordinates.lng !== nextProps.cities[i].coordinates.lng) {
      return false;
    }
  }

  // Quick cityColors comparison
  if (prevProps.cityColors.length !== nextProps.cityColors.length) return false;
  for (let i = 0; i < prevProps.cityColors.length; i++) {
    if (prevProps.cityColors[i].city !== nextProps.cityColors[i].city ||
        prevProps.cityColors[i].color !== nextProps.cityColors[i].color) {
      return false;
    }
  }

  // Preview location comparison
  if (prevProps.previewLocation !== nextProps.previewLocation) {
    if (!prevProps.previewLocation || !nextProps.previewLocation) return false;
    if (prevProps.previewLocation.name !== nextProps.previewLocation.name ||
        prevProps.previewLocation.coordinates.lat !== nextProps.previewLocation.coordinates.lat ||
        prevProps.previewLocation.coordinates.lng !== nextProps.previewLocation.coordinates.lng) {
      return false;
    }
  }

  return true;
};

export default React.memo(MapComponent, arePropsEqual);
